/* eslint-disable no-plusplus */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;

  const s = String(v).replace('S/', '').replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(s);
  // eslint-disable-next-line no-restricted-globals
  return isNaN(n) ? 0 : n;
};

const formatMoney = (n) => `S/ ${(Math.round(n * 100) / 100).toFixed(2)}`;

const TollSelectorTable = ({ rows = [], onChange }) => {
  const [selectionById, setSelectionById] = useState(() => {
    const init = {};
    for (let i = 0; i < rows.length; i++) {
      init[rows[i].tollId] = 'none';
    }
    return init;
  });

  const [docsById, setDocsById] = useState({});

  useEffect(() => {
    if (!rows || rows.length === 0) return;

    setSelectionById((prev) => {
      const next = { ...prev };
      let changed = false;

      for (let i = 0; i < rows.length; i++) {
        const id = rows[i].tollId;
        if (next[id] === undefined) {
          next[id] = 'none';
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [rows]);

  const setMode = (tollId, mode) => {
    setSelectionById((prev) => ({
      ...prev,
      [tollId]: mode,
    }));
  };

  const toggleRow = (tollId, targetMode) => {
    const current = selectionById[tollId] || 'none';
    const nextMode = current === targetMode ? 'none' : targetMode;
    setMode(tollId, nextMode);
  };

  const isAllBothChecked = () => {
    if (!rows || rows.length === 0) return false;
    for (let i = 0; i < rows.length; i++) {
      const id = rows[i].tollId;
      if ((selectionById[id] || 'none') !== 'both') return false;
    }
    return true;
  };

  const toggleAllBoth = () => {
    const allOn = isAllBothChecked();
    const next = {};
    for (let i = 0; i < rows.length; i++) {
      next[rows[i].tollId] = allOn ? 'none' : 'both';
    }
    setSelectionById(next);
  };

  const handleDocChange = (tollId, field, value) => {
    setDocsById((prev) => ({
      ...prev,
      [tollId]: {
        ...(prev[tollId] || { documentSeries: '', documentCode: '' }),
        [field]: value,
      },
    }));
  };

  const computed = useMemo(() => {
    let globalPeaje = 0;
    let globalDet = 0;

    let selectedPeaje = 0;
    let selectedDet = 0;

    const selectedRows = [];

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const mode = selectionById[r.tollId] || 'none';

      const doc = docsById[r.tollId] || {
        documentSeries: '',
        documentCode: '',
      };

      const totalAmount = toNumber(r.totalAmount);
      const detractionAmount = toNumber(r.detractionAmount);
      const tollAmount = totalAmount - detractionAmount;

      globalPeaje += tollAmount;
      globalDet += detractionAmount;

      const tollApplied = mode === 'both' || mode === 'peaje';
      const detApplied = mode === 'both' || mode === 'detraction';

      if (tollApplied) selectedPeaje += tollAmount;
      if (detApplied) selectedDet += detractionAmount;

      selectedRows.push({
        tollId: r.tollId,
        tollName: r.tollName,

        tollAmount,
        tollAmountApplied: tollApplied,

        detractionAmount,
        detractionApplied: detApplied,

        totalAmount,

        documentSeries: doc.documentSeries.trim() || null,
        documentCode: doc.documentCode.trim() || null,
      });
    }

    return {
      selectedRows,
      globalTotal: globalPeaje + globalDet,
      selectedTotal: selectedPeaje + selectedDet,
      global: { peaje: globalPeaje, detraction: globalDet },
      selected: { peaje: selectedPeaje, detraction: selectedDet },
    };
  }, [rows, selectionById, docsById]);

  const lastEmitRef = useRef({
    signature: '',
    globalTotal: null,
    selectedTotal: null,
  });

  useEffect(() => {
    if (typeof onChange !== 'function') return;

    const sigParts = [];
    for (let i = 0; i < rows.length; i++) {
      const id = rows[i].tollId;
      const doc = docsById[id] || { documentSeries: '', documentCode: '' };
      sigParts.push(
        `${String(id)}:${String(selectionById[id] || 'none')}:${
          doc.documentSeries
        }:${doc.documentCode}`,
      );
    }
    const signature = sigParts.join('|');

    const prev = lastEmitRef.current;

    if (
      prev.signature === signature &&
      prev.globalTotal === computed.globalTotal &&
      prev.selectedTotal === computed.selectedTotal
    ) {
      return;
    }

    lastEmitRef.current = {
      signature,
      globalTotal: computed.globalTotal,
      selectedTotal: computed.selectedTotal,
    };

    onChange({
      selectedRows: computed.selectedRows,
      globalTotal: computed.globalTotal,
      selectedTotal: computed.selectedTotal,
      global: computed.global,
      selected: computed.selected,
    });
  }, [rows, selectionById, docsById, computed, onChange]);

  return (
    <div className="mt-3">
      <div className="table-responsive">
        <table className="table table-bordered align-middle mb-0">
          <thead>
            <tr>
              <th style={{ minWidth: 90, textAlign: 'center' }}>
                <div>Todos</div>
                <input
                  type="checkbox"
                  checked={isAllBothChecked()}
                  onChange={toggleAllBoth}
                />
                <div style={{ fontSize: 12 }}>Peaje+Det</div>
              </th>

              <th style={{ minWidth: 260 }}>Estación de Peaje</th>
              <th style={{ minWidth: 140, textAlign: 'right' }}>RUC</th>
              <th style={{ minWidth: 140, textAlign: 'right' }}>Peaje</th>
              <th style={{ minWidth: 140, textAlign: 'right' }}>Detracción</th>
              <th style={{ minWidth: 140, textAlign: 'right' }}>Total</th>
              <th style={{ minWidth: 140 }}>Serie</th>
              <th style={{ minWidth: 140 }}>Codigo</th>

              <th style={{ minWidth: 110, textAlign: 'center' }}>Ambos</th>
              <th style={{ minWidth: 110, textAlign: 'center' }}>Solo Peaje</th>
              <th style={{ minWidth: 130, textAlign: 'center' }}>
                Solo Detracción
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => {
              const mode = selectionById[r.tollId] || 'none';
              const total = toNumber(r.totalAmount);
              const det = toNumber(r.detractionAmount);
              const peaje = total - det;
              const rowTotal = total;

              return (
                <tr key={r.tollId}>
                  <td style={{ textAlign: 'center' }}>{r.tollId}</td>
                  <td>{r.tollName}</td>
                  <td>{r.businessRuc}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(peaje)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(det)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatMoney(rowTotal)}
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="text"
                      placeholder="Serie"
                      onChange={(e) =>
                        handleDocChange(
                          r.tollId,
                          'documentSeries',
                          e.target.value,
                        )
                      }
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="text"
                      placeholder="Código / Ticket"
                      onChange={(e) =>
                        handleDocChange(
                          r.tollId,
                          'documentCode',
                          e.target.value,
                        )
                      }
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={mode === 'both'}
                      onChange={() => toggleRow(r.tollId, 'both')}
                    />
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={mode === 'peaje'}
                      onChange={() => toggleRow(r.tollId, 'peaje')}
                    />
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={mode === 'detraction'}
                      onChange={() => toggleRow(r.tollId, 'detraction')}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TollSelectorTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      tollId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      tollName: PropTypes.string.isRequired,
      detractionAmount: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
  onChange: PropTypes.func,
};

TollSelectorTable.defaultProps = {
  onChange: null,
};

export default TollSelectorTable;
